import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';
import {Client, ClientAccess, ClientAsset, ClientPolicy, ClientsState} from '../../types';

// Initial state
const initialState: ClientsState = {
  clients: [],
  selectedClient: null,
  userAccess: [],
  isLoading: false,
  isFetchingCrowdstrikeTickets: false,
  crowdstrikeTicketsMessage: null,
  policy: null,
  policyLoading: false,
  assets: [],
  assetsLoading: false,
  error: null,
};

// Async thunks
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/clients');
      return response.data.clients;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/clients/${clientId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client');
    }
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData: { name: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/clients', clientData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create client');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
    async ({clientId, clientData}: {
        clientId: string;
        clientData: {
            name?: string;
            description?: string;
            crowdstrike_client_id?: string | null;
            crowdstrike_client_secret?: string | null;
        }
    }, {rejectWithValue}) => {
    try {
        const response = await api.put(`/clients/${clientId}`, clientData);

        return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update client');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (clientId: string, { rejectWithValue }) => {
    try {
        await api.delete(`/clients/${clientId}`);
        return clientId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
    }
  }
);

export const fetchClientAccess = createAsyncThunk(
  'clients/fetchClientAccess',
  async (clientId: string, { rejectWithValue }) => {
    try {
        const response = await api.get(`/clients/${clientId}/access`);
        return response.data.users;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client access');
    }
  }
);

export const addClientAccess = createAsyncThunk(
  'clients/addClientAccess',
  async ({ clientId, userId }: { clientId: string; userId: string }, { rejectWithValue }) => {
    try {
        const response = await api.post(`/clients/${clientId}/access`, { user_id: userId });
        return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add client access');
    }
  }
);

export const removeClientAccess = createAsyncThunk(
  'clients/removeClientAccess',
  async ({ clientId, userId }: { clientId: string; userId: string }, { rejectWithValue }) => {
    try {
        await api.delete(`/clients/${clientId}/access/${userId}`);
        return { clientId, userId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove client access');
    }
  }
);

export const fetchClientPolicy = createAsyncThunk(
  'clients/fetchClientPolicy',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/clients/${clientId}/policy`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch policy');
    }
  }
);

export const upsertClientPolicy = createAsyncThunk(
  'clients/upsertClientPolicy',
  async ({ clientId, policy }: { clientId: string; policy: { auto_ticket_severities: string[]; custom_rules?: any[] } }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/clients/${clientId}/policy`, policy);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save policy');
    }
  }
);

export const fetchClientAssets = createAsyncThunk(
  'clients/fetchClientAssets',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/clients/${clientId}/assets`);
      return response.data.assets;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assets');
    }
  }
);

export const importClientAssetsCsv = createAsyncThunk(
  'clients/importClientAssetsCsv',
  async ({ clientId, file }: { clientId: string; file: File }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/clients/${clientId}/assets/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Import failed');
    }
  }
);

export const importClientAssetsJson = createAsyncThunk(
  'clients/importClientAssetsJson',
  async ({ clientId, assets }: { clientId: string; assets: any[] }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/clients/${clientId}/assets/import`, assets);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Import failed');
    }
  }
);

export const deleteClientAsset = createAsyncThunk(
  'clients/deleteClientAsset',
  async ({ clientId, assetId }: { clientId: string; assetId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/clients/${clientId}/assets/${assetId}`);
      return assetId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset');
    }
  }
);

export const clearClientAssets = createAsyncThunk(
  'clients/clearClientAssets',
  async (clientId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/clients/${clientId}/assets`);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear assets');
    }
  }
);

export const fetchCrowdstrikeTickets = createAsyncThunk(
    'clients/fetchCrowdstrikeTickets',
    async (clientId: string, {rejectWithValue}) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await api.post(`/clients/${clientId}/fetch_crowdstrike_tickets`, {});

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch tickets from CrowdStrike');
        }
    }
);

// Clients slice
const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearClientError: (state) => {
      state.error = null;
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch clients
    builder.addCase(fetchClients.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchClients.fulfilled, (state, action: PayloadAction<Client[]>) => {
      state.isLoading = false;
      state.clients = action.payload;
    });
    builder.addCase(fetchClients.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch client by ID
    builder.addCase(fetchClientById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchClientById.fulfilled, (state, action: PayloadAction<Client>) => {
      state.isLoading = false;
      state.selectedClient = action.payload;
    });
    builder.addCase(fetchClientById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Create client
    builder.addCase(createClient.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createClient.fulfilled, (state, action: PayloadAction<Client>) => {
      state.isLoading = false;
      state.clients.push(action.payload);
    });
    builder.addCase(createClient.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update client
    builder.addCase(updateClient.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateClient.fulfilled, (state, action: PayloadAction<Client>) => {
      state.isLoading = false;
      const index = state.clients.findIndex(client => client.id === action.payload.id);
      if (index !== -1) {
        state.clients[index] = action.payload;
      }
      if (state.selectedClient?.id === action.payload.id) {
        state.selectedClient = action.payload;
      }
    });
    builder.addCase(updateClient.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete client
    builder.addCase(deleteClient.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteClient.fulfilled, (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.clients = state.clients.filter(client => client.id !== action.payload);
      if (state.selectedClient?.id === action.payload) {
        state.selectedClient = null;
      }
    });
    builder.addCase(deleteClient.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch client access
    builder.addCase(fetchClientAccess.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchClientAccess.fulfilled, (state, action: PayloadAction<any[]>) => {
      state.isLoading = false;
      // The API returns user objects, not ClientAccess objects
      // We'll handle this in the component
    });
    builder.addCase(fetchClientAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Add client access
    builder.addCase(addClientAccess.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addClientAccess.fulfilled, (state, action: PayloadAction<ClientAccess>) => {
      state.isLoading = false;
      state.userAccess.push(action.payload);
    });
    builder.addCase(addClientAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Remove client access
    builder.addCase(removeClientAccess.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(removeClientAccess.fulfilled, (state, action: PayloadAction<{ clientId: string; userId: string }>) => {
      state.isLoading = false;
      state.userAccess = state.userAccess.filter(
        access => !(access.client_id === action.payload.clientId && access.user_id === action.payload.userId)
      );
    });
    builder.addCase(removeClientAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

      // Policy
      builder.addCase(fetchClientPolicy.pending, (state) => { state.policyLoading = true; });
      builder.addCase(fetchClientPolicy.fulfilled, (state, action: PayloadAction<ClientPolicy>) => {
        state.policyLoading = false;
        state.policy = action.payload;
      });
      builder.addCase(fetchClientPolicy.rejected, (state) => { state.policyLoading = false; });

      builder.addCase(upsertClientPolicy.pending, (state) => { state.policyLoading = true; });
      builder.addCase(upsertClientPolicy.fulfilled, (state, action: PayloadAction<ClientPolicy>) => {
        state.policyLoading = false;
        state.policy = action.payload;
      });
      builder.addCase(upsertClientPolicy.rejected, (state) => { state.policyLoading = false; });

      // Assets
      builder.addCase(fetchClientAssets.pending, (state) => { state.assetsLoading = true; });
      builder.addCase(fetchClientAssets.fulfilled, (state, action: PayloadAction<ClientAsset[]>) => {
        state.assetsLoading = false;
        state.assets = action.payload;
      });
      builder.addCase(fetchClientAssets.rejected, (state) => { state.assetsLoading = false; });

      builder.addCase(importClientAssetsCsv.pending, (state) => { state.assetsLoading = true; });
      builder.addCase(importClientAssetsCsv.fulfilled, (state) => { state.assetsLoading = false; });
      builder.addCase(importClientAssetsCsv.rejected, (state) => { state.assetsLoading = false; });

      builder.addCase(importClientAssetsJson.pending, (state) => { state.assetsLoading = true; });
      builder.addCase(importClientAssetsJson.fulfilled, (state) => { state.assetsLoading = false; });
      builder.addCase(importClientAssetsJson.rejected, (state) => { state.assetsLoading = false; });

      builder.addCase(deleteClientAsset.fulfilled, (state, action: PayloadAction<string>) => {
        state.assets = state.assets.filter(a => a.id !== action.payload);
      });

      builder.addCase(clearClientAssets.fulfilled, (state) => { state.assets = []; });

      // Fetch CrowdStrike tickets
      builder.addCase(fetchCrowdstrikeTickets.pending, (state) => {
          state.isFetchingCrowdstrikeTickets = true;
          state.crowdstrikeTicketsMessage = null;
          state.error = null;
      });
      builder.addCase(fetchCrowdstrikeTickets.fulfilled, (state, action) => {
          state.isFetchingCrowdstrikeTickets = false;
          state.crowdstrikeTicketsMessage = action.payload.message;
      });
      builder.addCase(fetchCrowdstrikeTickets.rejected, (state, action) => {
          state.isFetchingCrowdstrikeTickets = false;
          state.error = action.payload as string;
      });
  },
});

export const { clearClientError, clearSelectedClient } = clientsSlice.actions;
export default clientsSlice.reducer;
